import { FormControl, FormGroup, Validators } from '@angular/forms';
import JSON5 from 'json5';

import type { BaseObjectModel } from '@app/shared/models/base.model';

export enum RISK_MITIGATION_METHOD {
    NOT_APPLICABLE = 'not-applicable',
    INPUT_GUARDRAILS = 'input-guardrails',
    OUTPUT_GUARDRAILS = 'output-guardrails',
    PROMPT_INSTRUCTIONS = 'prompt-instructions',
    OTHER = 'other',
}

export interface Risk {
    name: string;
    key: string;
    description: string;
}

export interface RiskCategory {
    name: string;
    key: string;
    icon: string;
    color: string;
    risks: Risk[];
}

export interface RiskFindingAdapter {
    id?: number;
    riskCategory: string;
    risk: string;
    riskMitigationMethod: RISK_MITIGATION_METHOD[];
    explanation?: string;
    otherValue?: string;
}

export interface RiskAssessmentAdapter extends BaseObjectModel {
    projectSlug?: string | null;
    pipelineVersionId: number;
    findings?: RiskFindingAdapter[];
    config?: string;
}

export type RiskAssessmentFormGroup = ReturnType<typeof getRiskAssessmentForm>;

export function getRiskFormGroup(risk: RiskFindingAdapter) {
    return new FormGroup({
        id: new FormControl(risk?.id || null),
        riskMitigationMethod: new FormControl<RISK_MITIGATION_METHOD[]>(
            risk?.riskMitigationMethod || [],
        ),
        otherValue: new FormControl<string>(risk?.otherValue || null),
        explanation: new FormControl<string>(risk?.explanation || null),
    });
}

export function getRiskAssessmentForm(item: RiskAssessmentAdapter, riskCategories: RiskCategory[]) {
    return new FormGroup({
        id: new FormControl<number>(item.id || null),
        name: new FormControl<string>(item.name || null, Validators.required),
        pipelineVersionId: new FormControl<number>(item.pipelineVersionId || null),
        config: new FormControl<string>(item?.config || null),
        findings: new FormGroup(
            Object.fromEntries(
                (riskCategories || []).map(riskCategory => [
                    riskCategory.key,
                    new FormGroup(
                        Object.fromEntries(
                            riskCategory.risks.map(risk => {
                                const matchedRisk = (item.findings || []).find(
                                    finding =>
                                        finding.riskCategory === riskCategory.key &&
                                        finding.risk === risk.key,
                                );

                                return [risk.key, getRiskFormGroup(matchedRisk)];
                            }),
                        ),
                    ),
                ]),
            ),
        ),
    });
}

export function getRiskAssessmentForAPI(
    data: RiskAssessmentFormGroup['value'] & {
        comment?: string;
        versionName?: string;
        projectSlug?: string | null;
    },
): RiskAssessmentAdapter {
    const findings = Object.entries(data.findings || {}).flatMap(([categoryKey, categoryGroup]) =>
        Object.entries(categoryGroup || {}).map(([riskKey, value]) => ({
            id: value.id,
            riskCategory: categoryKey,
            risk: riskKey,
            riskMitigationMethod: value.riskMitigationMethod,
            explanation: value.explanation,
            otherValue: value.otherValue,
        })),
    );

    return {
        id: data.id,
        name: data.name,
        findings,
        pipelineVersionId: data.pipelineVersionId,
        config: data.config,
        projectSlug: data.projectSlug,
        comment: data?.comment,
        versionName: data?.versionName,
    };
}

export function parseRiskAssessmentConfig(config: string): RiskCategory[] {
    try {
        return JSON5.parse(config || '[]');
    } catch (error) {
        console.error(error);
        return [];
    }
}
